import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function PluginsPage(): Node {
  return html`
    <div>
      <h1>Plugins</h1>
      <p><code>createPluginHost</code> enables an extensible plugin architecture with sandboxed permissions and lifecycle hooks.</p>

      <h2>Creating a Plugin Host</h2>
      ${CodeBlock(`import { createPluginHost } from 'onefold/plugin';

const plugins = createPluginHost({
  permissions: ['read:state', 'write:state', 'ui:render'],
});`)}

      <h2>Registering Plugins</h2>
      ${CodeBlock(`plugins.register({
  name: 'analytics-plugin',
  version: '1.0.0',
  permissions: ['read:state'],
  hooks: {
    onInit: (ctx) => {
      console.log('Analytics plugin initialized');
    },
    onRouteChange: (ctx, route) => {
      trackPageView(route.path);
    },
    onStateChange: (ctx, state) => {
      trackEvent('state-change', state);
    },
  },
});

plugins.register({
  name: 'theme-plugin',
  version: '1.0.0',
  permissions: ['read:state', 'ui:render'],
  hooks: {
    onInit: (ctx) => {
      ctx.provide('theme-toggle', () => toggleTheme());
    },
  },
});`)}

      <h2>Permissions</h2>
      <p>Plugins can only access APIs they've been granted permission for:</p>
      <table>
        <tr><th>Permission</th><th>Description</th></tr>
        <tr><td><code>read:state</code></td><td>Read application state.</td></tr>
        <tr><td><code>write:state</code></td><td>Modify application state.</td></tr>
        <tr><td><code>ui:render</code></td><td>Render UI elements into slots.</td></tr>
        <tr><td><code>network</code></td><td>Make HTTP requests.</td></tr>
        <tr><td><code>storage</code></td><td>Access localStorage/sessionStorage.</td></tr>
      </table>

      ${Callout('A plugin that requests a permission not in the host\'s allowed list is rejected at registration time.')}

      <h2>Lifecycle Hooks</h2>
      <table>
        <tr><th>Hook</th><th>When</th></tr>
        <tr><td><code>onInit</code></td><td>Plugin is registered and ready.</td></tr>
        <tr><td><code>onDestroy</code></td><td>Plugin is unregistered.</td></tr>
        <tr><td><code>onRouteChange</code></td><td>Navigation occurs.</td></tr>
        <tr><td><code>onStateChange</code></td><td>Application state updates.</td></tr>
        <tr><td><code>onError</code></td><td>Unhandled error in the app.</td></tr>
      </table>

      <h2>Plugin Context</h2>
      ${CodeBlock(`// The ctx object provides scoped access:
ctx.provide(key, value)  // expose a value to other plugins
ctx.consume(key)         // read a value from another plugin
ctx.getState()           // read state (if permitted)
ctx.setState(partial)    // update state (if permitted)`)}

      <h2>Try It</h2>
      <p>Register plugins, start/stop them, and see lifecycle events in real time:</p>

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { createPluginHost } from 'onefold/plugin';

function App() {
  const logs = createSignal([]);
  const log = (msg) => logs.set(prev => [...prev.slice(-8), msg]);

  const plugins = createPluginHost();

  // Listen to host events
  plugins.on('plugin:started', (name) => log('Started: ' + name));
  plugins.on('plugin:stopped', (name) => log('Stopped: ' + name));
  plugins.on('plugin:error', (name, err) => log('Error in ' + name + ': ' + err));

  // Register plugins
  plugins.register({
    name: 'analytics',
    version: '1.0.0',
    permissions: ['observe'],
    setup: (ctx) => {
      log('[analytics] setup called');
      ctx.on('track', (data) => log('[analytics] tracked: ' + JSON.stringify(data)));
      return () => log('[analytics] teardown');
    },
  });

  plugins.register({
    name: 'logger',
    version: '1.0.0',
    permissions: ['observe'],
    setup: (ctx) => {
      log('[logger] setup called');
      return () => log('[logger] teardown');
    },
  });

  return html\`
    <div>
      <h3>Plugin System — createPluginHost</h3>
      <div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap">
        <button onclick=\${() => plugins.start()}>Start All</button>
        <button onclick=\${() => plugins.stop()}>Stop All</button>
        <button onclick=\${() => plugins.startPlugin('analytics')}>Start analytics</button>
        <button onclick=\${() => plugins.stopPlugin('logger')}>Stop logger</button>
      </div>
      <div style="margin-bottom:12px">
        <p style="font-size:13px;font-weight:600;margin-bottom:6px">Registered: \${() => plugins.list().join(', ')}</p>
        <div style="display:flex;gap:8px">
          \${() => plugins.list().map(name => html\`
            <span style="padding:3px 8px;border-radius:4px;font-size:12px;background:\${plugins.getStatus(name) === 'active' ? '#dcfce7' : '#f1f5f9'};color:\${plugins.getStatus(name) === 'active' ? '#166534' : '#64748b'}">
              \${name}: \${plugins.getStatus(name)}
            </span>
          \`)}
        </div>
      </div>
      <div style="background:#0f172a;color:#a5f3fc;padding:12px;border-radius:8px;font-family:monospace;font-size:12px;max-height:180px;overflow-y:auto">
        \${() => logs().length === 0
          ? html\`<div style="color:#64748b">Click "Start All" to begin...</div>\`
          : logs().map(l => html\`<div style="padding:2px 0">> \${l}</div>\`)
        }
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'createPluginHost — Lifecycle Management')}
    </div>
  `;
}
